// ActionListEditor.js
import React, { useState, useEffect } from "react";
import { useSelector } from "react-redux";

import {
  Box,
  Typography,
  TextField,
  Button,
  Paper,
  FormControl,
  FormControlLabel,
  FormLabel,
  Radio,
  RadioGroup,
  IconButton,
} from "@mui/material";
import DeleteIcon from "@mui/icons-material/Delete";
import {
  AssignmentTurnedIn as FollowUpIcon,
  Description as FileIcon,
  Chat as CommunicateIcon,
  Warning as EscalateIcon,
} from "@mui/icons-material";

// Action node configurations
const ACTION_CONFIGS = {
  "Arrange Monitoring or Follow up": {
    template: (values) =>
      values.info_var
        ? `Leave for GP. Info: ${values.info_var}`
        : "Leave for GP",
    fields_config: [
      {
        label: "Task type:",
        var: "task_type",
        widget: "TextField",
        type: "string",
        width: 300,
      },
      {
        label: "Due date:",
        var: "due_date",
        widget: "TextField",
        type: "string",
        width: 300,
      },
      {
        label: "Notes:",
        var: "notes",
        widget: "TextField",
        type: "string",
        width: 300,
      },
      {
        label: "Assign to:",
        var: "clinician_name",
        widget: "TextField",
        type: "string",
        width: 300,
      },
    ],
    validate: () => ({}),
  },
  "File Report": {
    template: (values) => {
      if (values.filing_approach === "No Comments")
        return "File report without comments";
      if (values.filing_comment)
        return `File report with comment: "${values.filing_comment}"`;
      return "File report";
    },
    fields_config: [
      {
        label: "Filing Approach:",
        var: "filing_approach",
        widget: "RadioButton",
        type: "text",
        values: ["With Comment", "No Comments"],
      },
      {
        label: "Filing Comment:",
        var: "filing_comment",
        widget: "TextField",
        type: "string",
        width: 300,
      },
    ],
    validate: () => ({}),
  },
  "Communicate with Patient": {
    template: (values) =>
      values.accurx_message
        ? `Communicate with Patient: "${values.accurx_message}"`
        : "Communicate with Patient",
    fields_config: [
      {
        label: "Message:",
        var: "accurx_message",
        widget: "TextField",
        type: "string",
        width: 300,
      },
    ],
    validate: () => ({}),
  },
  // "Escalate or Urgent": {
  //   template: (values) =>
  //     values.new_owner ? `Change Owner to ${values.new_owner}` : "Change Owner",
  //   fields_config: [
  //     {
  //       label: "Change Owner To:",
  //       var: "new_owner",
  //       widget: "TextField",
  //       type: "string",
  //       width: 200,
  //     },
  //   ],
  //   validate: () => ({}),
  // },
};

const AVAILABLE_ACTIONS = Object.keys(ACTION_CONFIGS);

/**
 * ActionListEditor Component
 *
 * Props:
 * - actions: array of action nodes (initial state)
 * - onChange: callback when actions list changes
 * - readOnly: boolean to disable editing
 */
const ActionListEditor = ({
  actions = [],
  onChange = () => {},
  readOnly = false,
}) => {
  const [actionList, setActionList] = useState(actions);
  const [errors, setErrors] = useState({});
  // const [selectedActionType, setSelectedActionType] = useState(
  //   AVAILABLE_ACTIONS[0],
  // );

  const has_write = useSelector(
    (state) => state.authen.loggedInUser?.license_role === "Change",
  );

  // Update parent when local state changes
  useEffect(() => {
    onChange(actionList);
  }, [actionList, onChange]);

  // Validate all actions
  useEffect(() => {
    const newErrors = {};
    actionList.forEach((action) => {
      const config = ACTION_CONFIGS[action.node];
      if (config && config.validate) {
        const fieldErrors = config.validate(action.fields || {});
        if (Object.keys(fieldErrors).length > 0) {
          newErrors[action.id] = fieldErrors;
        }
      }
    });
    setErrors(newErrors);
  }, [actionList]);

  // const addAction = () => {
  //   const newAction = {
  //     id: `action_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`,
  //     node: selectedActionType,
  //     fields: {},
  //   };
  //   setActionList([...actionList, newAction]);
  // };

  const updateActionField = (actionId, field, value) => {
    setActionList(
      actionList.map((action) =>
        action.id === actionId
          ? {
              ...action,
              fields: { ...action.fields, [field]: value },
            }
          : action,
      ),
    );
  };

  const deleteAction = (actionId) => {
    if (window.confirm("Are you sure you want to delete this action?")) {
      setActionList(actionList.filter((action) => action.id !== actionId));
      const newErrors = { ...errors };
      delete newErrors[actionId];
      setErrors(newErrors);
    }
  };

  const renderField = (action, field) => {
    const value = action.fields?.[field.var] ?? "";
    const width = field.width || 200;
    const actionErrors = errors[action.id] || {};

    const commonSx = {
      minWidth: width,
      "& .MuiInputBase-input": { fontSize: "0.85rem" },
      "& .MuiInputLabel-root": { fontSize: "0.85rem" },
      "& .MuiFormHelperText-root": { fontSize: "0.85rem" },
    };

    if (field.widget === "RadioButton") {
      return (
        <FormControl
          component="fieldset"
          error={!!actionErrors[field.var]}
          sx={{ ...commonSx, minHeight: 69 }}
          disabled={readOnly}
        >
          <FormLabel component="legend" sx={{ fontSize: "0.85rem", mb: 0.5 }}>
            {field.label}
          </FormLabel>
          <RadioGroup
            row
            value={value}
            onChange={(e) =>
              updateActionField(action.id, field.var, e.target.value)
            }
          >
            {field.values?.map((v) => (
              <FormControlLabel
                key={v}
                value={v}
                control={<Radio size="small" />}
                label={v}
                sx={{ "& .MuiFormControlLabel-label": { fontSize: "0.85rem" } }}
              />
            ))}
          </RadioGroup>
        </FormControl>
      );
    }

    return (
      <TextField
        label={field.label}
        value={value}
        type={
          field.type === "float" || field.type === "int" ? "number" : "text"
        }
        size="small"
        onChange={(e) =>
          updateActionField(action.id, field.var, e.target.value)
        }
        sx={{ width: field.width ?? 200, ...commonSx }}
        error={!!actionErrors[field.var]}
        helperText={actionErrors[field.var] || " "}
        disabled={readOnly}
      />
    );
  };

  return (
    <Box sx={{ width: "100%" }}>
      <Box sx={{ mb: 2 }}>
        <Typography
          variant="h6"
          sx={{ fontSize: "1rem", fontWeight: 600, mb: 1 }}
        >
          Action List
        </Typography>

        {!readOnly && (
          <Box
            sx={{
              display: "flex",
              flexWrap: "wrap",
              gap: 1.5,
            }}
          >
            {AVAILABLE_ACTIONS.map((actionType) => {
              const ICONS = {
                "Arrange Monitoring or Follow up": (
                  <FollowUpIcon fontSize="small" />
                ),
                "File Report": <FileIcon fontSize="small" />,
                "Communicate with Patient": (
                  <CommunicateIcon fontSize="small" />
                ),
                "Escalate or Urgent": <EscalateIcon fontSize="small" />,
              };

              return (
                <Paper
                  key={actionType}
                  onClick={() => {
                    const newAction = {
                      id: `action_${Date.now()}_${Math.random()
                        .toString(36)
                        .substr(2, 9)}`,
                      node: actionType,
                      fields: {},
                    };
                    setActionList((prev) => [...prev, newAction]);
                  }}
                  sx={{
                    cursor: "pointer",
                    display: "flex",
                    alignItems: "center",
                    gap: 1,
                    px: 1.5,
                    py: 1,
                    border: "1px solid #ddd",
                    borderRadius: 2,
                    backgroundColor: "#fafafa",
                    pointerEvents: has_write ? "auto" : "none",
                    opacity: has_write ? 1 : 0.6,
                    transition: "all 0.2s ease",
                    "&:hover": {
                      backgroundColor: "#e3f2fd",
                      borderColor: "#90caf9",
                    },
                  }}
                  elevation={0}
                >
                  {ICONS[actionType]}
                  <Typography
                    variant="body2"
                    sx={{ fontSize: "0.85rem", fontWeight: 500 }}
                  >
                    {actionType}
                  </Typography>
                </Paper>
              );
            })}
          </Box>
        )}
      </Box>

      {actionList.length === 0 ? (
        <Paper
          sx={{
            p: 3,
            textAlign: "center",
            backgroundColor: "#f5f5f5",
            border: "1px dashed #ccc",
          }}
        >
          <Typography variant="body2" color="textSecondary">
            No actions configured. Click "Add Action" to get started.
          </Typography>
        </Paper>
      ) : (
        <Box sx={{ display: "flex", flexDirection: "column", gap: 1.5 }}>
          {actionList.map((action, index) => {
            const config = ACTION_CONFIGS[action.node];
            if (!config) return null;

            return (
              <Paper
                key={action.id}
                sx={{
                  p: 1.5,
                  background: "#fff",
                  border: "1px solid #e0e0e0",
                  pointerEvents: has_write ? "auto" : "none",
                }}
                elevation={1}
              >
                <Box
                  sx={{
                    display: "flex",
                    alignItems: "center",
                    gap: 1,
                    mb: 1.5,
                  }}
                >
                  <Button
                    size="small"
                    variant="outlined"
                    sx={{
                      minWidth: 36,
                      width: 36,
                      height: 28,
                      fontSize: "0.8rem",
                    }}
                  >
                    {index + 1}
                  </Button>
                  <Typography
                    variant="subtitle2"
                    sx={{ fontSize: "0.85rem", fontWeight: 500 }}
                  >
                    {action.node}
                  </Typography>
                  <Box sx={{ flexGrow: 1 }} />
                  {!readOnly && (
                    <IconButton
                      size="small"
                      color="error"
                      onClick={() => deleteAction(action.id)}
                    >
                      <DeleteIcon fontSize="small" />
                    </IconButton>
                  )}
                </Box>

                <Box
                  sx={{
                    display: "flex",
                    flexWrap: "wrap",
                    gap: 1,
                    alignItems: "center",
                  }}
                >
                  {config.fields_config?.map((field) => (
                    <Box
                      key={field.var}
                      sx={{
                        flexBasis:
                          field.widget === "RadioButton" ? "100%" : "auto",
                      }}
                    >
                      {renderField(action, field)}
                    </Box>
                  ))}
                </Box>
              </Paper>
            );
          })}
        </Box>
      )}
    </Box>
  );
};

export default ActionListEditor;
